// src/pages/DashboardPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  LinearProgress,
  CircularProgress,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Divider,
  TableSortLabel,
  Alert,
  IconButton,
  Collapse,
} from '@mui/material';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import CloseIcon     from '@mui/icons-material/Close';
import {
  People as PeopleIcon,
  Assessment as AssessmentIcon,
  Assignment as AssignmentIcon,
  Business as BusinessIcon,
  ExitToApp as LogoutIcon,
  VerifiedUser as VerificationIcon,
  TrendingUp as TrendingIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as PendingIcon,
  Warning as WarningIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  LocationOn as LocationIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import nisLogoWithText from '../assets/images/immgrationlogo1.png';
import OfficerSearchBox from '../components/OfficerSearchBox';

// ---------------------------------------------------------
// STAT CARD - reusable mini component
// ---------------------------------------------------------
const StatCard = ({ title, value, subtitle, icon, color = 'primary' }) => {
  const colorMap = {
    primary: { bg: '#e3f2fd', text: '#1976d2' },
    success: { bg: '#e8f5e9', text: '#2e7d32' },
    warning: { bg: '#fff3e0', text: '#ed6c02' },
    error:   { bg: '#ffebee', text: '#d32f2f' },
  };
  const c = colorMap[color] || colorMap.primary;

  return (
    <Card
      elevation={2}
      sx={{
        height: '100%',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 },
      }}
    >
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box flex={1}>
            <Typography variant="body2" color="text.secondary" fontWeight={500} gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4" fontWeight="bold" sx={{ my: 1 }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <Avatar sx={{ bgcolor: c.bg, color: c.text, width: 56, height: 56 }}>
            {icon}
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------
// MAIN DASHBOARD PAGE
// ---------------------------------------------------------
// -- Inline component for admin transfer request rows ------
const TransferRequestRow = ({ req, onApprove, onReject }) => {
  const [rejectNote, setRejectNote] = React.useState('');
  const [showReject, setShowReject] = React.useState(false);

  return (
    <Box sx={{ p: 2, mb: 1, bgcolor: 'white', borderRadius: 1, border: '1px solid #ffcc80' }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
        <Box flex={1}>
          <Typography variant="body1" fontWeight="bold">
            {req.personnel.fullName} — {req.personnel.serviceNumber}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {req.personnel.rankName} &nbsp;|&nbsp;
            From: <strong>{req.fromCommand.code}</strong> ({req.fromCommand.zone}) →
            To: <strong>{req.toCommand.code}</strong> ({req.toCommand.zone})
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            Requested by: {req.requestedBy.name} &nbsp;|&nbsp; {req.timeAgo}
          </Typography>
          <Typography variant="caption" color="warning.dark" display="block" sx={{ mt: 0.5 }}>
            Officer's reason: {req.officerReason}
          </Typography>
          {req.personnel.verificationStatus === 'VERIFIED' && (
            <Chip label="⚠️ Already Verified" size="small" color="error" variant="outlined" sx={{ mt: 0.5 }} />
          )}
        </Box>
        <Box display="flex" gap={1} ml={2} flexShrink={0}>
          <Button size="small" variant="contained" color="success"
            onClick={() => onApprove(req.id)}
            sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>
            ✅ Approve
          </Button>
          <Button size="small" variant="outlined" color="error"
            onClick={() => setShowReject(!showReject)}
            sx={{ textTransform: 'none' }}>
            ❌ Reject
          </Button>
        </Box>
      </Box>
      {showReject && (
        <Box sx={{ mt: 1, p: 1.5, bgcolor: 'error.50', borderRadius: 1 }}>
          <TextField fullWidth size="small" label="Rejection reason *"
            value={rejectNote} onChange={(e) => setRejectNote(e.target.value)}
            sx={{ mb: 1 }} />
          <Box display="flex" gap={1}>
            <Button size="small" variant="contained" color="error"
              disabled={!rejectNote.trim()}
              onClick={() => { onReject(req.id, rejectNote); setShowReject(false); }}
              sx={{ textTransform: 'none' }}>
              Confirm Rejection
            </Button>
            <Button size="small" variant="outlined"
              onClick={() => setShowReject(false)}
              sx={{ textTransform: 'none' }}>Cancel</Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

const DashboardPage = () => {
  const navigate = useNavigate();
  const { user, logout, isAdmin } = useAuth();

  const isOfficer    = user?.role === 'VERIFICATION_OFFICER';
  const isManagement = user?.role === 'MANAGEMENT';

  // -- Admin dashboard state --
  const [adminStats, setAdminStats]         = useState(null);
  const [zones, setZones]                   = useState([]);
  const [commands, setCommands]             = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [dailyStats, setDailyStats]         = useState(null);
  const [adminLoading, setAdminLoading]     = useState(false);
  const [adminError, setAdminError]         = useState(null);
  const [refreshing, setRefreshing]         = useState(false);

  // -- Command table state --
  const [search, setSearch]         = useState('');
  const [zoneFilter, setZoneFilter] = useState('ALL');
  const [sortBy, setSortBy]         = useState('zone');
  const [sortOrder, setSortOrder]   = useState('asc');

  // -- Officer dashboard state --
  const [officerData, setOfficerData]       = useState(null);
  const [officerLoading, setOfficerLoading] = useState(false);
  const [officerError, setOfficerError]     = useState(null);
  const [officerCommands, setOfficerCommands]   = useState([]);
  const [completionDialog, setCompletionDialog] = useState({ open: false, command: null });
  const [openRequestDialog, setOpenRequestDialog] = useState({ open: false, command: null });
  const [transferDialog,     setTransferDialog]     = useState({ open: false });
  const [transferSearch,     setTransferSearch]     = useState('');
  const [transferResults,    setTransferResults]    = useState([]);
  const [transferSearching,  setTransferSearching]  = useState(false);
  const [transferSelected,   setTransferSelected]   = useState(null);
  const [transferReason,     setTransferReason]     = useState('');
  const [transferSubmitting, setTransferSubmitting] = useState(false);
  const [transferRequests,   setTransferRequests]   = useState([]);
  const [myTransferRequests, setMyTransferRequests] = useState([]);
  const [completionLoading, setCompletionLoading] = useState(false);

  // -- Admin command requests state --
  const [commandRequests, setCommandRequests]     = useState([]);
  
  // -- Collapsible sections state (collapsed by default) - ADMIN ONLY --
  const [commandMgmtOpen, setCommandMgmtOpen] = useState(false);
  const [adminZoneProgressOpen, setAdminZoneProgressOpen] = useState(false);
  const [allAdminCommands, setAllAdminCommands]   = useState([]);
  const [rejectDialog, setRejectDialog]           = useState({ open: false, command: null });
  const [rejectReason, setRejectReason]           = useState('');
  const [commandActionLoading, setCommandActionLoading] = useState(false);

  // -- Management state --
  const [mgmtData, setMgmtData]       = useState(null);
  const [mgmtLoading, setMgmtLoading] = useState(false);
  const [mgmtError, setMgmtError]     = useState(null);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // -- Fetch Officer dashboard data --
  const fetchOfficerData = async ({ silent = false } = {}) => {
    if (!silent) setOfficerLoading(true);
    try {
      const token = localStorage.getItem('token');
      const cfg   = { headers: { Authorization: `Bearer ${token}` } };
      const res   = await axios.get('/api/dashboard/officer/stats', cfg);
      setOfficerData(res.data.data);
      setOfficerError(null);
    } catch (err) {
      console.error('Officer dashboard error:', err);
      setOfficerError(err.response?.data?.message || 'Failed to load your dashboard data.');
    } finally {
      setOfficerLoading(false);
    }
  };

  // -- Fetch Officer commands --
  const fetchTransferRequests = async () => {
    try {
      const token = localStorage.getItem('token');
      const cfg = { headers: { Authorization: `Bearer ${token}` } };
      const res = await axios.get('/api/dashboard/admin/transfer-requests', cfg);
      setTransferRequests(res.data.data || []);
    } catch (err) { console.error('Transfer requests error:', err); }
  };

  const fetchMyTransferRequests = async () => {
    try {
      const token = localStorage.getItem('token');
      const cfg = { headers: { Authorization: `Bearer ${token}` } };
      const res = await axios.get('/api/dashboard/officer/transfer-requests', cfg);
      setMyTransferRequests(res.data.data || []);
    } catch (err) { console.error('My transfer requests error:', err); }
  };

  const fetchOfficerCommands = async () => {
    try {
      const token = localStorage.getItem('token');
      const cfg = { headers: { Authorization: `Bearer ${token}` } };
      const res = await axios.get('/api/dashboard/officer/commands', cfg);
      // res.data.data = { activeCommand, allCommands, zone }
      setOfficerCommands(res.data.data || { activeCommand: null, allCommands: [], zone: null });
    } catch (err) {
      console.error('Officer commands error:', err);
    }
  };

  // -- Request command OPEN (officer requests to start) --
  const handleRequestOpen = async (commandId) => {
    setCompletionLoading(true);
    try {
      const token = localStorage.getItem('token');
      const cfg = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`/api/dashboard/officer/commands/${commandId}/request-open`, {}, cfg);
      await fetchOfficerCommands();
      setOpenRequestDialog({ open: false, command: null });
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to request command open');
    } finally {
      setCompletionLoading(false);
    }
  };

  // -- Request command completion --
  const handleRequestCompletion = async (commandId) => {
    setCompletionLoading(true);
    try {
      const token = localStorage.getItem('token');
      const cfg = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`/api/dashboard/officer/commands/${commandId}/request-completion`, {}, cfg);
      await fetchOfficerCommands();
      setCompletionDialog({ open: false, command: null });
    } catch (err) {
      console.error('Request completion error:', err);
    } finally {
      setCompletionLoading(false);
    }
  };

  // -- Admin: fetch all commands (for command management panel) --
  const fetchAllAdminCommands = async () => {
    try {
      const token = localStorage.getItem('token');
      const cfg = { headers: { Authorization: `Bearer ${token}` } };
      const res = await axios.get('/api/dashboard/admin/all-commands', cfg);
      setAllAdminCommands(res.data.data || []);
    } catch (err) {
      console.error('All commands error:', err);
    }
  };

  // -- Admin: open a command (set IN_PROGRESS) --


  // -- Admin: fetch command requests --
  const fetchCommandRequests = async () => {
    try {
      const token = localStorage.getItem('token');
      const cfg = { headers: { Authorization: `Bearer ${token}` } };
      const res = await axios.get('/api/dashboard/admin/command-requests', cfg);
      setCommandRequests(res.data.data || []);
    } catch (err) {
      console.error('Command requests error:', err);
    }
  };

  // -- Officer: search personnel globally --
  const handleTransferSearch = async (query) => {
    setTransferSearch(query);
    if (query.trim().length < 2) { setTransferResults([]); return; }
    setTransferSearching(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`/api/dashboard/officer/personnel/search?q=${encodeURIComponent(query)}`,
        { headers: { Authorization: `Bearer ${token}` } });
      setTransferResults(res.data.data?.results || []);
    } catch (err) {
      if (err.response?.status === 403) {
        alert(err.response.data.message);
        setTransferDialog({ open: false });
      }
    } finally { setTransferSearching(false); }
  };

  // -- Officer: submit transfer request --
  const handleSubmitTransfer = async () => {
    if (!transferSelected || !transferReason.trim()) return;
    setTransferSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `/api/dashboard/officer/personnel/${transferSelected.id}/request-transfer`,
        { reason: transferReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert(res.data.message);
      setTransferDialog({ open: false });
      setTransferSearch(''); setTransferResults([]);
      setTransferSelected(null); setTransferReason('');
      fetchMyTransferRequests();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to submit request');
    } finally { setTransferSubmitting(false); }
  };

  // -- Admin: approve transfer request --
  const handleApproveTransfer = async (requestId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`/api/dashboard/admin/transfer-requests/${requestId}/approve`, {},
        { headers: { Authorization: `Bearer ${token}` } });
      alert(res.data.message);
      fetchTransferRequests();
    } catch (err) { alert(err.response?.data?.message || 'Failed to approve'); }
  };

  // -- Admin: reject transfer request --
  const handleRejectTransfer = async (requestId, note) => {
    if (!note?.trim()) { alert('Please provide a rejection reason'); return; }
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`/api/dashboard/admin/transfer-requests/${requestId}/reject`,
        { note }, { headers: { Authorization: `Bearer ${token}` } });
      alert(res.data.message);
      fetchTransferRequests();
    } catch (err) { alert(err.response?.data?.message || 'Failed to reject'); }
  };

  // -- Admin: force-close a command directly --
  const handleForceClose = async (commandId) => {
    if (!window.confirm('Force-close this command? Officers will not be able to verify further.')) return;
    setCommandActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      const cfg = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`/api/dashboard/admin/commands/${commandId}/force-close`, {}, cfg);
      await fetchAllAdminCommands();
      await fetchCommandRequests();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to force close');
    } finally {
      setCommandActionLoading(false);
    }
  };

  // -- Admin: reopen a completed command --
  const handleReopenCommand = async (commandId) => {
    if (!window.confirm('Reopen this completed command? Officers will be able to continue verifying.')) return;
    setCommandActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      const cfg = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`/api/dashboard/admin/commands/${commandId}/reopen`, {}, cfg);
      await fetchAllAdminCommands();
      await fetchCommandRequests();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to reopen');
    } finally {
      setCommandActionLoading(false);
    }
  };

  // -- Admin: cancel any pending request --
  const handleCancelRequest = async (commandId) => {
    if (!window.confirm('Cancel this pending request? The command will revert to its previous state.')) return;
    setCommandActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      const cfg = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`/api/dashboard/admin/commands/${commandId}/cancel-request`, {}, cfg);
      await fetchAllAdminCommands();
      await fetchCommandRequests();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to cancel request');
    } finally {
      setCommandActionLoading(false);
    }
  };

  // -- Admin: approve command OPEN request --
  const handleApproveOpen = async (commandId) => {
    setCommandActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      const cfg = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`/api/dashboard/admin/commands/${commandId}/approve-open`, {}, cfg);
      await fetchAllAdminCommands();
      await fetchCommandRequests();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to approve');
    } finally {
      setCommandActionLoading(false);
    }
  };

  // -- Admin: reject command OPEN request --
  const handleRejectOpen = async (commandId) => {
    setCommandActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      const cfg = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`/api/dashboard/admin/commands/${commandId}/reject-open`, { reason: rejectReason }, cfg);
      await fetchAllAdminCommands();
      await fetchCommandRequests();
      setRejectDialog({ open: false, command: null });
      setRejectReason('');
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to reject');
    } finally {
      setCommandActionLoading(false);
    }
  };

  // -- Admin: approve command completion --
  const handleApproveCommand = async (commandId) => {
    setCommandActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      const cfg = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`/api/dashboard/admin/commands/${commandId}/approve`, {}, cfg);
      await fetchCommandRequests();
      await fetchAdminData();
    } catch (err) {
      console.error('Approve error:', err);
    } finally {
      setCommandActionLoading(false);
    }
  };

  // -- Admin: reject command completion --
  const handleRejectCommand = async () => {
    if (!rejectDialog.command) return;
    setCommandActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      const cfg = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`/api/dashboard/admin/commands/${rejectDialog.command.id}/reject`,
        { reason: rejectReason }, cfg);
      await fetchCommandRequests();
      setRejectDialog({ open: false, command: null });
      setRejectReason('');
    } catch (err) {
      console.error('Reject error:', err);
    } finally {
      setCommandActionLoading(false);
    }
  };

  // -- Fetch Admin dashboard data --
  const fetchAdminData = async ({ silent = false } = {}) => {
    try {
      if (!silent) setRefreshing(true);
      const token = localStorage.getItem('token');
      const cfg   = { headers: { Authorization: `Bearer ${token}` } };

      const [statsRes, zonesRes, cmdsRes, actRes, dailyRes] = await Promise.all([
        axios.get('/api/dashboard/admin/stats', cfg),
        axios.get('/api/dashboard/admin/zones', cfg),
        axios.get('/api/dashboard/admin/commands', cfg),
        axios.get('/api/dashboard/admin/recent-activity?limit=20', cfg),
        axios.get('/api/dashboard/admin/daily-stats?days=7', cfg),
      ]);

      setAdminStats(statsRes.data.data);
      setZones(zonesRes.data.data);
      setCommands(cmdsRes.data.data);
      setRecentActivity(actRes.data.data);
      setDailyStats(dailyRes.data.data);
      setAdminLoading(false);
      setRefreshing(false);
      setAdminError(null);
    } catch (err) {
      console.error('Admin dashboard error:', err);
      setAdminError(err.response?.data?.message || 'Failed to load dashboard data');
      setAdminLoading(false);
      setRefreshing(false);
    }
  };

  // -- Fetch Management statistics --
  const fetchMgmtStatistics = async ({ silent = false } = {}) => {
    if (!silent) setMgmtLoading(true);
    try {
      const token = localStorage.getItem('token');
      const cfg   = { headers: { Authorization: `Bearer ${token}` } };
      const res   = await axios.get('/api/dashboard/management/stats', cfg);
      setMgmtData(res.data.data);
      setMgmtError(null);
    } catch (err) {
      console.error('Management dashboard error:', err);
      setMgmtError(err.response?.data?.message || 'Failed to load dashboard data.');
    } finally {
      setMgmtLoading(false);
    }
  };

    useEffect(() => {
    if (!user) return;
 
    if (isAdmin) {
      fetchAdminData();
      fetchCommandRequests();
      fetchAllAdminCommands();
      fetchTransferRequests();
      const interval = setInterval(() => {
        // Only auto-refresh if the page is visible (user is looking at it)
        if (!document.hidden) {
          fetchAdminData({ silent: true });
          fetchCommandRequests();
          fetchAllAdminCommands();
          fetchTransferRequests();
        }
      }, 60000); // 60 seconds
      return () => clearInterval(interval);
    }
    if (isOfficer) {
      fetchOfficerData();
      fetchOfficerCommands();
      fetchMyTransferRequests();
      const interval = setInterval(() => {
        if (!document.hidden) {
          fetchOfficerData({ silent: true });
          fetchOfficerCommands();
        }
      }, 60000); // 60 seconds
      return () => clearInterval(interval);
    }
    if (isManagement) {
      fetchMgmtStatistics();
      const interval = setInterval(() => {
        if (!document.hidden) {
          fetchMgmtStatistics({ silent: true });
        }
      }, 120000); // 2 minutes
      return () => clearInterval(interval);
    }
  }, [user, isAdmin, isOfficer, isManagement]);

  // -- Command table helpers --
  const uniqueZones = ['ALL', ...new Set(commands.map((c) => c.zone))];

  const filteredCommands = commands
    .filter((cmd) => {
      const matchSearch = cmd.name.toLowerCase().includes(search.toLowerCase()) ||
                          cmd.code.toLowerCase().includes(search.toLowerCase());
      const matchZone   = zoneFilter === 'ALL' || cmd.zone === zoneFilter;
      return matchSearch && matchZone;
    })
    .sort((a, b) => {
      let cmp = 0;
      if (sortBy === 'zone')     cmp = a.zone.localeCompare(b.zone);
      if (sortBy === 'name')     cmp = a.name.localeCompare(b.name);
      if (sortBy === 'progress') cmp = a.progressPercentage - b.progressPercentage;
      if (sortBy === 'total')    cmp = a.totalPersonnel - b.totalPersonnel;
      return sortOrder === 'asc' ? cmp : -cmp;
    });

  const handleSort = (col) => {
    if (sortBy === col) setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortOrder('asc'); }
  };

  const getStatusColor = (status) => {
    if (status === 'COMPLETED')  return 'success';
    if (status === 'IN_PROGRESS') return 'warning';
    return 'default';
  };

  const getProgressColor = (pct) =>
    pct >= 80 ? 'success' : pct >= 50 ? 'warning' : 'error';

  // ========================================================
  // MANAGEMENT DASHBOARD
  // ========================================================
  if (isManagement) {
    const ov    = mgmtData?.overview;
    const zones = mgmtData?.zoneProgress || [];
    const cmds  = mgmtData?.topCommands  || [];
    const trend = mgmtData?.dailyTrend   || [];

    return (
      <Container maxWidth="xl">
        <Box sx={{ mt: 4, mb: 6 }}>

          {/* Header */}
          <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box display="flex" alignItems="center" gap={2.5}>
                {/* NIS Logo */}
                <Box
                  component="img"
                  src={nisLogoWithText}
                  alt="Nigeria Immigration Service"
                  sx={{
                    height: 50,
                    width: 'auto',
                    objectFit: 'contain',
                  }}
                />
                <Box>
                  <Typography 
                    variant="h6" 
                    fontWeight="600" 
                    sx={{ 
                      color: '#00a651', 
                      lineHeight: 1.2, 
                      mb: 0.5,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      fontSize: '1.1rem'
                    }}
                  >
                    Management Dashboard
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                    {user?.firstName} {user?.lastName}
                    {user?.serviceNumber && <> • SN: <strong>{user.serviceNumber}</strong></>}
                    {mgmtData?.exercise?.name && <> • {mgmtData.exercise.name}</>}
                  </Typography>
                </Box>
              </Box>
              <Box display="flex" gap={2}>
                <IconButton onClick={fetchMgmtStatistics} title="Refresh">
                  <RefreshIcon />
                </IconButton>
                <Button variant="outlined" color="error" startIcon={<LogoutIcon />} onClick={handleLogout}>
                  Logout
                </Button>
              </Box>
            </Box>
          </Paper>

          {mgmtError && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setMgmtError(null)}>{mgmtError}</Alert>
          )}
                    <OfficerSearchBox />

          {mgmtLoading ? (
            <Box display="flex" justifyContent="center" p={8}><CircularProgress size={60} /></Box>
          ) : (
            <>
              {/* -- Row 1: Stat Cards -- */}
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <StatCard title="Total Personnel" value={ov?.totalPersonnel?.toLocaleString() || '0'}
                    icon={<PeopleIcon />} color="primary" />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <StatCard title="Verified" value={ov?.totalVerified?.toLocaleString() || '0'}
                    subtitle={`${ov?.overallProgress || 0}% Complete`}
                    icon={<CheckCircleIcon />} color="success" />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <StatCard title="Pending" value={ov?.totalPending?.toLocaleString() || '0'}
                    subtitle="Awaiting verification"
                    icon={<PendingIcon />} color="warning" />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <StatCard title="Today's Verifications" value={ov?.todayVerified?.toLocaleString() || '0'}
                    subtitle={`${ov?.activeOfficersToday || 0} officers active`}
                    icon={<TrendingIcon />} color="info" />
                </Grid>
              </Grid>

              {/* -- Row 2: Overall Progress + Trend -- */}
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} md={4}>
                  <Paper elevation={3} sx={{ p: 3, height: '100%' }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '1rem' }}>Overall Progress</Typography>
                    <Box display="flex" justifyContent="center" alignItems="center" sx={{ py: 3 }}>
                      <Box position="relative" display="inline-flex">
                        <CircularProgress variant="determinate" value={ov?.overallProgress || 0}
                          size={160} thickness={6}
                          sx={{ color: ov?.overallProgress >= 80 ? 'success.main' : ov?.overallProgress >= 50 ? 'warning.main' : 'error.main' }} />
                        <Box position="absolute" top={0} left={0} bottom={0} right={0}
                          display="flex" alignItems="center" justifyContent="center" flexDirection="column">
                          <Typography variant="h4" fontWeight="bold" color="text.primary">
                            {ov?.overallProgress || 0}%
                          </Typography>
                          <Typography variant="caption" color="text.secondary">Complete</Typography>
                        </Box>
                      </Box>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    {[
                      { label: 'Total Personnel', value: ov?.totalPersonnel?.toLocaleString() || 0, color: 'text.primary' },
                      { label: 'Verified',         value: ov?.totalVerified?.toLocaleString()  || 0, color: 'success.main' },
                      { label: 'Pending',          value: ov?.totalPending?.toLocaleString()   || 0, color: 'warning.main' },
                      { label: 'Complaints',       value: ov?.totalComplaints?.toLocaleString()|| 0, color: 'error.main'   },
                    ].map(({ label, value, color }) => (
                      <Box key={label} display="flex" justifyContent="space-between" py={0.8}
                        sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Typography variant="body2" color="text.secondary">{label}</Typography>
                        <Typography variant="body2" fontWeight="bold" color={color}>{value}</Typography>
                      </Box>
                    ))}
                  </Paper>
                </Grid>

                <Grid item xs={12} md={8}>
                  <Paper elevation={3} sx={{ p: 3, height: '100%' }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '1rem' }}>14-Day Verification Trend</Typography>
                    {trend.length > 0 ? (
                      <ResponsiveContainer width="100%" height={280}>
                        <LineChart data={trend}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" tick={{ fontSize: 11 }}
                            tickFormatter={(d) => { const dt = new Date(d); return `${dt.getMonth()+1}/${dt.getDate()}`; }} />
                          <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                          <Tooltip labelFormatter={(d) => new Date(d).toLocaleDateString()} />
                          <Line type="monotone" dataKey="count" stroke="#1a237e" strokeWidth={2}
                            dot={{ fill: '#1a237e', r: 4 }} name="Verifications" />
                        </LineChart>
                      </ResponsiveContainer>
                    ) : (
                      <Box textAlign="center" py={6}>
                        <Typography color="text.secondary">No trend data available</Typography>
                      </Box>
                    )}
                  </Paper>
                </Grid>
              </Grid>

              {/* -- Row 3: Zone Progress -- */}
              <Paper elevation={3} sx={{ p: 4, mb: 4, borderRadius: 2 }}>
                <Box display="flex" alignItems="center" gap={2} mb={3}>
                  <Box sx={{ p: 1.5, backgroundColor: 'success.light', borderRadius: 2 }}>
                    <TrendingIcon sx={{ fontSize: 28, color: 'success.dark' }} />
                  </Box>
                  <Typography variant="h6" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '1rem' }}>Verification Progress by Zone</Typography>
                </Box>
                <Grid container spacing={2}>
                  {zones.map((zone) => (
                    <Grid item xs={12} md={6} key={zone.zone}>
                      <Box sx={{ mb: 1 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                          <Typography variant="body1" fontWeight={600} noWrap sx={{ maxWidth: '60%' }}>
                            {zone.zone}
                          </Typography>
                          <Box display="flex" gap={2} alignItems="center">
                            <Typography variant="body2" color="text.secondary">
                              {zone.verified?.toLocaleString()} / {zone.total?.toLocaleString()}
                            </Typography>
                            <Typography variant="body1" fontWeight="bold" color="primary.main" sx={{ minWidth: 50, textAlign: 'right' }}>
                              {zone.percentage}%
                            </Typography>
                          </Box>
                        </Box>
                        <LinearProgress variant="determinate" value={zone.percentage}
                          sx={{ height: 10, borderRadius: 5, backgroundColor: 'grey.200',
                            '& .MuiLinearProgress-bar': { borderRadius: 5,
                              background: zone.percentage >= 75 ? 'linear-gradient(90deg, #4caf50, #66bb6a)' :
                                          zone.percentage >= 50 ? 'linear-gradient(90deg, #ff9800, #ffa726)' :
                                                                  'linear-gradient(90deg, #f44336, #ef5350)' }}} />
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Paper>

              {/* -- Row 4: Top Commands -- */}
              <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
                <Box display="flex" alignItems="center" gap={2} mb={3}>
                  <Box sx={{ p: 1.5, backgroundColor: 'info.light', borderRadius: 2 }}>
                    <BusinessIcon sx={{ fontSize: 28, color: 'info.dark' }} />
                  </Box>
                  <Typography variant="h6" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '1rem' }}>Top Commands by Completion</Typography>
                </Box>
                {cmds.map((command, index) => (
                  <Box key={command.code} sx={{ mb: 2.5 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Box sx={{ width: 32, height: 32, borderRadius: '50%', backgroundColor: 'primary.main',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: 'white', fontWeight: 'bold', fontSize: '0.875rem', flexShrink: 0 }}>
                          {index + 1}
                        </Box>
                        <Box>
                          <Typography variant="body1" fontWeight={600}>{command.code} — {command.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{command.zone}</Typography>
                        </Box>
                      </Box>
                      <Box display="flex" gap={2} alignItems="center">
                        <Typography variant="body2" color="text.secondary">
                          {command.verified?.toLocaleString()} / {command.total?.toLocaleString()}
                        </Typography>
                        <Typography variant="h6" fontWeight="bold" color="primary.main" sx={{ minWidth: 60, textAlign: 'right' }}>
                          {command.percentage}%
                        </Typography>
                      </Box>
                    </Box>
                    <LinearProgress variant="determinate" value={command.percentage}
                      sx={{ height: 8, borderRadius: 5, backgroundColor: 'grey.200',
                        '& .MuiLinearProgress-bar': { borderRadius: 5,
                          background: command.percentage >= 75 ? 'linear-gradient(90deg, #4caf50, #66bb6a)' :
                                      command.percentage >= 50 ? 'linear-gradient(90deg, #ff9800, #ffa726)' :
                                                                 'linear-gradient(90deg, #f44336, #ef5350)' }}} />
                  </Box>
                ))}
              </Paper>
            </>
          )}
        </Box>

      </Container>
    );
  }

  // ========================================================
  // ADMIN DASHBOARD
  // ========================================================
  if (isAdmin) {
    const pendingOpen      = (allAdminCommands || []).filter(c => c.status === 'PENDING_OPEN').length;
    const pendingClose     = commandRequests.length;
    const pendingTransfers = transferRequests.length;
    const totalPending     = pendingOpen + pendingClose + pendingTransfers;
    const actionCards = [
      { label: 'Start Requests',    count: pendingOpen,      icon: '📋',
        desc:     pendingOpen > 0 ? `${pendingOpen} command${pendingOpen > 1 ? 's' : ''} awaiting approval to open` : 'No pending start requests',
        bgColor:  pendingOpen > 0 ? '#e3f2fd' : '#f5f5f5', bdColor: pendingOpen > 0 ? '#1976d2' : '#e0e0e0', txtColor: pendingOpen > 0 ? '#0d47a1' : 'text.secondary' },
      { label: 'Close Requests',    count: pendingClose,     icon: '⏳',
        desc:     pendingClose > 0 ? `${pendingClose} command${pendingClose > 1 ? 's' : ''} awaiting approval to close` : 'No pending close requests',
        bgColor:  pendingClose > 0 ? '#fff8e1' : '#f5f5f5', bdColor: pendingClose > 0 ? '#f57f17' : '#e0e0e0', txtColor: pendingClose > 0 ? '#e65100' : 'text.secondary' },
      { label: 'Transfer Requests', count: pendingTransfers, icon: '🔄',
        desc:     pendingTransfers > 0 ? `${pendingTransfers} personnel transfer${pendingTransfers > 1 ? 's' : ''} awaiting review` : 'No pending transfer requests',
        bgColor:  pendingTransfers > 0 ? '#fff3e0' : '#f5f5f5', bdColor: pendingTransfers > 0 ? '#f57c00' : '#e0e0e0', txtColor: pendingTransfers > 0 ? '#bf360c' : 'text.secondary' },
    ];
    return (
      <Container maxWidth="xl">
        <Box sx={{ mt: 4, mb: 6 }}>

          {/* Header */}
          <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box display="flex" alignItems="center" gap={2.5}>
                {/* NIS Logo */}
                <Box
                  component="img"
                  src={nisLogoWithText}
                  alt="Nigeria Immigration Service"
                  sx={{
                    height: 50,
                    width: 'auto',
                    objectFit: 'contain',
                  }}
                />
                <Box>
                  <Typography 
                    variant="h6" 
                    fontWeight="600" 
                    sx={{ 
                      color: '#00a651', 
                      lineHeight: 1.2, 
                      mb: 0.5,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      fontSize: '1.1rem'
                    }}
                  >
                    Admin Dashboard
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                    {user?.serviceNumber && <>SN: <strong>{user.serviceNumber}</strong> • </>}
                    {adminStats?.exerciseName || 'Exercise'} — Overview & Statistics
                  </Typography>
                </Box>
              </Box>
              <Box display="flex" gap={2}>
                <IconButton onClick={fetchAdminData} disabled={refreshing} title="Refresh">
                  <RefreshIcon />
                </IconButton>
                <Button variant="outlined" color="error" startIcon={<LogoutIcon />} onClick={handleLogout}>
                  Logout
                </Button>
              </Box>
            </Box>
          </Paper>

          {adminError && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setAdminError(null)}>{adminError}</Alert>
          )}

          {adminLoading ? (
            <Box display="flex" justifyContent="center" p={8}><CircularProgress size={60} /></Box>
          ) : (
            <>
              {/* -- Row 1: Quick Actions (prominent horizontal row) -- */}
              <Paper sx={{ p: 3, mb: 4 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '1rem' }}>Quick Actions</Typography>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  {[
                    { label: 'Manage Users',       path: '/users',        color: 'primary',   icon: <PeopleIcon />       },
                    { label: 'Manage Exercises',   path: '/exercises',    color: 'secondary', icon: <AssessmentIcon />   },
                    { label: 'Manage Commands',    path: '/commands',     color: 'info',      icon: <BusinessIcon />     },
                    { label: 'Manage Personnel',   path: '/personnel',    color: 'success',   icon: <AssignmentIcon />   },
                    { label: 'Start Verification', path: '/verification', color: 'warning',   icon: <VerificationIcon /> },
                    { label: 'Flagged Recaptures', path: '/flagged-recaptures', color: 'warning',   icon: <VerificationIcon /> },
                    { label: 'View Reports',       path: '/reports',      color: 'error',     icon: <TrendingIcon />     },
                  ].map((action) => (
                    <Grid size={{ xs: 6, sm: 4, md: 'grow' }} key={action.label}>
                      <Button variant="contained" color={action.color}
                        onClick={() => navigate(action.path)} fullWidth
                        startIcon={action.icon}
                        sx={{ py: 1.5, height: '100%', lineHeight: 1.25, textTransform: 'none', fontWeight: 'bold', fontSize: '0.8rem' }}>
                        {action.label}
                      </Button>
                    </Grid>
                  ))}
                </Grid>
              </Paper>

              {/* -- Row 2: Stat Cards -- */}
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <StatCard title="Total Personnel" value={adminStats?.totalPersonnel?.toLocaleString() || '0'}
                    icon={<PeopleIcon />} color="primary" />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <StatCard title="Verified" value={adminStats?.verified?.toLocaleString() || '0'}
                    subtitle={`${adminStats?.progressPercentage || 0}% Complete`}
                    icon={<CheckCircleIcon />} color="success" />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <StatCard title="Pending" value={adminStats?.pending?.toLocaleString() || '0'}
                    icon={<PendingIcon />} color="warning" />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <StatCard title="Complaints" value={(adminStats?.complaintsPending ?? adminStats?.complaintsCount)?.toLocaleString() || '0'}
                    subtitle={`${adminStats?.preClearedCount || 0} Pre-cleared`}
                    icon={<WarningIcon />} color="error" />
                </Grid>
              </Grid>

              {/* -- Row 3: Action Required Cards -- */}
              <Box sx={{ mb: 4 }}>
                {totalPending > 0 && (
                  <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'error.main',
                      animation: 'pulse 1.5s infinite', '@keyframes pulse': {
                        '0%': { opacity: 1 }, '50%': { opacity: 0.3 }, '100%': { opacity: 1 }
                      }}} />
                    <Typography variant="caption" color="error.main" fontWeight="bold">
                      {totalPending} item{totalPending > 1 ? 's' : ''} requiring your attention
                    </Typography>
                  </Box>
                )}
                <Grid container spacing={2}>
                  {actionCards.map((card) => (
                    <Grid item xs={12} sm={4} key={card.label}>
                      <Paper
                        onClick={() => {
                          const el = document.getElementById('command-management-panel');
                          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }}
                        sx={{
                          p: 2.5,
                          border: '1px solid',
                          borderColor: card.bdColor,
                          bgcolor: card.bgColor,
                          borderRadius: 2,
                          cursor: card.count > 0 ? 'pointer' : 'default',
                          transition: 'box-shadow 0.2s',
                          '&:hover': card.count > 0 ? { boxShadow: 3 } : {},
                        }}>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                          <Box>
                            <Typography variant="h3" fontWeight="bold"
                              sx={{ color: card.count > 0 ? card.bdColor : 'text.disabled', lineHeight: 1 }}>
                              {card.count}
                            </Typography>
                            <Typography variant="body2" fontWeight="bold" sx={{ color: card.txtColor, mt: 0.5 }}>
                              {card.icon} {card.label}
                            </Typography>
                          </Box>
                          {card.count > 0 && (
                            <Chip label="Action needed" size="small"
                              sx={{ bgcolor: card.bdColor, color: 'white', fontWeight: 'bold', fontSize: '0.65rem' }} />
                          )}
                          {card.count === 0 && (
                            <Chip label="All clear ✓" size="small" variant="outlined"
                              sx={{ borderColor: '#bdbdbd', color: 'text.disabled', fontSize: '0.65rem' }} />
                          )}
                        </Box>
                        <Typography variant="caption" sx={{ color: card.txtColor, mt: 1, display: 'block' }}>
                          {card.desc}
                        </Typography>
                        {card.count > 0 && (
                          <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: 'block' }}>
                            Click to review ↓
                          </Typography>
                        )}
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Box>

              {/* -- Row 4: Command Management Panel -- */}
              <Paper id="command-management-panel" sx={{ p: 3, mb: 4 }}>
                <Box 
                  display="flex" 
                  alignItems="center" 
                  gap={2} 
                  mb={3}
                  onClick={() => setCommandMgmtOpen(!commandMgmtOpen)}
                  sx={{ 
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                      borderRadius: 1,
                    },
                    p: 1,
                    mx: -1,
                    transition: 'background-color 0.2s',
                  }}
                >
                  <Box sx={{ p: 1, bgcolor: 'primary.light', borderRadius: 1 }}>
                    <AssignmentIcon sx={{ color: 'primary.dark' }} />
                  </Box>
                  <Typography variant="h6" fontWeight="bold" sx={{ textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '1rem' }}>Command Management</Typography>
                  {(commandRequests.length + transferRequests.length) > 0 && (
                    <Chip label={`${commandRequests.length + transferRequests.length} Pending`} color="warning" size="small" sx={{ fontWeight: 'bold' }} />
                  )}
                  <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Button 
                      size="small" 
                      startIcon={<RefreshIcon />}
                      onClick={(e) => { e.stopPropagation(); fetchAllAdminCommands(); fetchCommandRequests(); }}
                    >
                      Refresh
                    </Button>
                    {commandMgmtOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </Box>
                </Box>

                <Collapse in={commandMgmtOpen}>
                  <Box>
                {/* -- Pending OPEN requests (officer wants to start) -- */}
                {(allAdminCommands || []).filter(c => c.status === 'PENDING_OPEN').length > 0 && (
                  <Box sx={{ mb: 3, p: 2, bgcolor: '#e3f2fd', border: '2px solid #1976d2', borderRadius: 2 }}>
                    <Typography variant="subtitle1" fontWeight="bold" color="primary.dark" gutterBottom>
                      📋 Start Requests — Officers want to begin these commands
                    </Typography>
                    {(allAdminCommands || []).filter(c => c.status === 'PENDING_OPEN').map((cmd) => (
                      <Box key={cmd.id} display="flex" alignItems="center" justifyContent="space-between"
                        sx={{ p: 2, mb: 1, bgcolor: 'white', borderRadius: 1, border: '1px solid #90caf9' }}>
                        <Box flex={1}>
                          <Typography variant="body1" fontWeight="bold">{cmd.code} — {cmd.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Zone: {cmd.zone} &nbsp;|&nbsp; {cmd.totalPersonnel} personnel to verify
                          </Typography>
                        </Box>
                        <Box display="flex" gap={1} ml={2}>
                          <Button size="small" variant="contained" color="primary"
                            disabled={commandActionLoading}
                            onClick={() => handleApproveOpen(cmd.id)}
                            sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>
                            ✅ Approve — Open Command
                          </Button>
                          <Button size="small" variant="outlined" color="error"
                            disabled={commandActionLoading}
                            onClick={() => setRejectDialog({ open: true, command: { ...cmd, type: 'open' } })}
                            sx={{ textTransform: 'none' }}>
                            ❌ Reject
                          </Button>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}

                {/* -- Pending COMPLETION requests (officer wants to close) -- */}
                {commandRequests.length > 0 && (
                  <Box sx={{ mb: 3, p: 2, bgcolor: 'warning.50', border: '2px solid', borderColor: 'warning.main', borderRadius: 2 }}>
                    <Typography variant="subtitle1" fontWeight="bold" color="warning.dark" gutterBottom>
                      ⏳ Completion Requests — Officers want to close these commands
                    </Typography>
                    {commandRequests.map((cmd) => (
                      <Box key={cmd.id} display="flex" alignItems="center" justifyContent="space-between"
                        sx={{ p: 2, mb: 1, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'warning.light' }}>
                        <Box flex={1}>
                          <Typography variant="body1" fontWeight="bold">{cmd.code} — {cmd.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Zone: {cmd.zone} &nbsp;|&nbsp; {cmd.verified}/{cmd.totalPersonnel} verified ({cmd.progressPercentage}%)
                            &nbsp;|&nbsp; Requested by {cmd.requestedBy} {cmd.timeAgo}
                          </Typography>
                          {cmd.pending > 0 && (
                            <Typography variant="caption" color="warning.dark" display="block">
                              ⚠️ {cmd.pending} personnel still unverified — will be marked Did Not Appear
                            </Typography>
                          )}
                        </Box>
                        <Box display="flex" gap={1} ml={2}>
                          <Button size="small" variant="contained" color="success"
                            disabled={commandActionLoading}
                            onClick={() => handleApproveCommand(cmd.id)}
                            sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>
                            ✅ Approve & Close
                          </Button>
                          <Button size="small" variant="outlined" color="error"
                            disabled={commandActionLoading}
                            onClick={() => setRejectDialog({ open: true, command: { ...cmd, type: 'completion' } })}
                            sx={{ textTransform: 'none' }}>
                            ❌ Reject
                          </Button>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}

                {/* -- Transfer Requests (orange box) -- */}
                {transferRequests.length > 0 && (
                  <Box sx={{ mb: 3, p: 2, bgcolor: '#fff3e0', border: '2px solid #f57c00', borderRadius: 2 }}>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ color: '#e65100' }} gutterBottom>
                      🔄 Personnel Transfer Requests — Officers requesting to move personnel
                    </Typography>
                    {transferRequests.map((req) => (
                      <TransferRequestRow key={req.id} req={req}
                        onApprove={handleApproveTransfer}
                        onReject={handleRejectTransfer} />
                    ))}
                  </Box>
                )}

                {/* Reject reason box — shared for both open and completion rejects */}
                {rejectDialog.open && (
                  <Box sx={{ mb: 3, p: 2, bgcolor: 'error.50', border: '1px solid', borderColor: 'error.light', borderRadius: 1 }}>
                    <Typography variant="body2" fontWeight="bold" color="error.dark" gutterBottom>
                      Rejecting: {rejectDialog.command?.name}
                    </Typography>
                    <TextField fullWidth size="small" label="Reason for rejection (optional)"
                      value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} sx={{ mb: 1.5 }} />
                    <Box display="flex" gap={2}>
                      <Button size="small" variant="contained" color="error" disabled={commandActionLoading}
                        onClick={() => rejectDialog.command?.type === 'open'
                          ? handleRejectOpen(rejectDialog.command.id)
                          : handleRejectCommand()}
                        sx={{ textTransform: 'none' }}>
                        {commandActionLoading ? 'Rejecting...' : 'Confirm Rejection'}
                      </Button>
                      <Button size="small" variant="outlined"
                        onClick={() => { setRejectDialog({ open: false, command: null }); setRejectReason(''); }}
                        sx={{ textTransform: 'none' }}>Cancel</Button>
                    </Box>
                  </Box>
                )}

                {/* All commands table */}
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  All Commands — Review and manage command status across all zones
                </Typography>
                {(() => {
                  // Group by zone
                  const grouped = (allAdminCommands || []).reduce((acc, cmd) => {
                    if (!acc[cmd.zone]) acc[cmd.zone] = [];
                    acc[cmd.zone].push(cmd);
                    return acc;
                  }, {});
                  return Object.entries(grouped).map(([zone, cmds]) => (
                    <Box key={zone} mb={3}>
                      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, color: 'primary.dark' }}>
                        {zone}
                      </Typography>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ bgcolor: 'grey.50' }}>
                              <TableCell><strong>Command</strong></TableCell>
                              <TableCell align="right"><strong>Total</strong></TableCell>
                              <TableCell align="right"><strong>Verified</strong></TableCell>
                              <TableCell align="right"><strong>Pending</strong></TableCell>
                              <TableCell><strong>Progress</strong></TableCell>
                              <TableCell><strong>Status</strong></TableCell>
                              <TableCell align="center"><strong>Action</strong></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {cmds.map((cmd) => (
                              <TableRow key={cmd.id}
                                sx={{ bgcolor: cmd.status === 'IN_PROGRESS' ? '#e8f5e9' :
                                               cmd.status === 'PENDING_COMPLETION' ? '#fff8e1' :
                                               cmd.status === 'COMPLETED' ? '#f5f5f5' : 'white' }}>
                                <TableCell>
                                  <Typography variant="body2" fontWeight="bold">{cmd.code}</Typography>
                                  <Typography variant="caption" color="text.secondary">{cmd.name}</Typography>
                                </TableCell>
                                <TableCell align="right">{cmd.totalPersonnel}</TableCell>
                                <TableCell align="right" sx={{ color: 'success.main', fontWeight: 'bold' }}>{cmd.verified}</TableCell>
                                <TableCell align="right" sx={{ color: cmd.pending > 0 ? 'warning.main' : 'inherit' }}>{cmd.pending}</TableCell>
                                <TableCell sx={{ minWidth: 130 }}>
                                  <Box display="flex" alignItems="center" gap={1}>
                                    <Box flex={1}>
                                      <LinearProgress variant="determinate" value={cmd.progressPercentage}
                                        color={cmd.progressPercentage >= 80 ? 'success' : cmd.progressPercentage >= 50 ? 'warning' : 'error'}
                                        sx={{ height: 6, borderRadius: 3 }} />
                                    </Box>
                                    <Typography variant="caption" fontWeight="bold">{cmd.progressPercentage}%</Typography>
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Chip size="small"
                                    label={cmd.status === 'IN_PROGRESS' ? '🟢 Active' :
                                           cmd.status === 'PENDING_COMPLETION' ? '⏳ Pending Close' :
                                           cmd.status === 'COMPLETED' ? '✅ Completed' : '⏸️ Not Started'}
                                    color={cmd.status === 'IN_PROGRESS' ? 'success' :
                                           cmd.status === 'PENDING_COMPLETION' ? 'warning' :
                                           cmd.status === 'COMPLETED' ? 'default' : 'default'}
                                    variant={cmd.status === 'COMPLETED' ? 'outlined' : 'filled'}
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  {cmd.status === 'NOT_STARTED' && (
                                    <Typography variant="caption" color="text.disabled">—</Typography>
                                  )}
                                  {cmd.status === 'PENDING_OPEN' && (
                                    <Box display="flex" flexDirection="column" gap={0.5}>
                                      <Typography variant="caption" color="primary.main" fontWeight="bold">📋 Pending Start</Typography>
                                      <Button size="small" variant="contained" color="primary"
                                        disabled={commandActionLoading}
                                        onClick={() => handleApproveOpen(cmd.id)}
                                        sx={{ textTransform: 'none', fontSize: '0.7rem', py: 0.3 }}>
                                        ✅ Approve Open
                                      </Button>
                                      <Button size="small" variant="outlined" color="warning"
                                        disabled={commandActionLoading}
                                        onClick={() => setRejectDialog({ open: true, command: { ...cmd, type: 'open' } })}
                                        sx={{ textTransform: 'none', fontSize: '0.7rem', py: 0 }}>
                                        ❌ Reject
                                      </Button>
                                      <Button size="small" variant="text" color="error"
                                        disabled={commandActionLoading}
                                        onClick={() => handleCancelRequest(cmd.id)}
                                        sx={{ textTransform: 'none', fontSize: '0.65rem', py: 0 }}>
                                        Cancel Request
                                      </Button>
                                    </Box>
                                  )}
                                  {cmd.status === 'IN_PROGRESS' && (
                                    <Box display="flex" flexDirection="column" gap={0.5}>
                                      <Typography variant="caption" color="success.main" fontWeight="bold">🟢 In progress</Typography>
                                      <Button size="small" variant="outlined" color="error"
                                        disabled={commandActionLoading}
                                        onClick={() => handleForceClose(cmd.id)}
                                        sx={{ textTransform: 'none', fontSize: '0.7rem', py: 0 }}>
                                        Force Close
                                      </Button>
                                    </Box>
                                  )}
                                  {cmd.status === 'PENDING_COMPLETION' && (
                                    <Box display="flex" flexDirection="column" gap={0.5}>
                                      <Typography variant="caption" color="warning.main" fontWeight="bold">⏳ Pending Close</Typography>
                                      <Button size="small" variant="contained" color="success"
                                        disabled={commandActionLoading}
                                        onClick={() => handleApproveCommand(cmd.id)}
                                        sx={{ textTransform: 'none', fontSize: '0.7rem', py: 0.3 }}>
                                        ✅ Approve & Close
                                      </Button>
                                      <Button size="small" variant="outlined" color="warning"
                                        disabled={commandActionLoading}
                                        onClick={() => setRejectDialog({ open: true, command: { ...cmd, type: 'completion' } })}
                                        sx={{ textTransform: 'none', fontSize: '0.7rem', py: 0 }}>
                                        ❌ Reject
                                      </Button>
                                      <Button size="small" variant="text" color="error"
                                        disabled={commandActionLoading}
                                        onClick={() => handleCancelRequest(cmd.id)}
                                        sx={{ textTransform: 'none', fontSize: '0.65rem', py: 0 }}>
                                        Cancel Request
                                      </Button>
                                    </Box>
                                  )}
                                  {cmd.status === 'COMPLETED' && (
                                    <Button size="small" variant="outlined" color="warning"
                                      disabled={commandActionLoading}
                                      onClick={() => handleReopenCommand(cmd.id)}
                                      sx={{ textTransform: 'none', fontSize: '0.7rem' }}>
                                      🔄 Reopen
                                    </Button>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  ))
                })()}
                  </Box>
                </Collapse>
              </Paper>

              {/* -- Row 5: Daily Statistics (full width) -- */}
              <Paper sx={{ p: 3, mb: 4 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '1rem' }}>Daily Statistics</Typography>
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} md={4}>
                    <Grid container spacing={2}>
                      <Grid item xs={4}>
                        <Box textAlign="center" sx={{ p: 1.5, border: 1, borderColor: 'primary.light', borderRadius: 2 }}>
                          <Typography variant="caption" color="text.secondary">Today</Typography>
                          <Typography variant="h4" fontWeight="bold" color="primary.main">
                            {dailyStats?.today?.verified ?? 0}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">verified</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={4}>
                        <Box textAlign="center" sx={{ p: 1.5, border: 1, borderColor: 'success.light', borderRadius: 2 }}>
                          <Typography variant="caption" color="text.secondary">This Week</Typography>
                          <Typography variant="h4" fontWeight="bold" color="success.main">
                            {dailyStats?.thisWeek?.verified ?? 0}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">verified</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={4}>
                        <Box textAlign="center" sx={{ p: 1.5, border: 1, borderColor: 'info.light', borderRadius: 2 }}>
                          <Typography variant="caption" color="text.secondary">Officers</Typography>
                          <Typography variant="h4" fontWeight="bold" color="info.main">
                            {dailyStats?.today?.activeOfficers ?? 0}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">active today</Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <Typography variant="body2" fontWeight="bold" mb={1}>7-Day Verification Trend</Typography>
                    {dailyStats?.dailyTrend?.length > 0 ? (
                      <ResponsiveContainer width="100%" height={180}>
                        <LineChart data={dailyStats.dailyTrend}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" tick={{ fontSize: 11 }}
                            tickFormatter={(d) => { const dt = new Date(d); return `${dt.getMonth()+1}/${dt.getDate()}`; }} />
                          <YAxis tick={{ fontSize: 11 }} />
                          <Tooltip labelFormatter={(d) => new Date(d).toLocaleDateString()} />
                          <Line type="monotone" dataKey="count" stroke="#1976d2" strokeWidth={2}
                            dot={{ fill: '#1976d2', r: 4 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    ) : (
                      <Box textAlign="center" py={3}>
                        <Typography color="text.secondary">No data yet for this period</Typography>
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </Paper>

              {/* -- Row 6: Zone Progress -- */}
              <Paper sx={{ p: 3, mb: 4 }}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  onClick={() => setAdminZoneProgressOpen(!adminZoneProgressOpen)}
                  sx={{
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                      borderRadius: 1,
                    },
                    p: 1,
                    mx: -1,
                    transition: 'background-color 0.2s',
                    mb: 2,
                  }}
                >
                  <Typography variant="h6" fontWeight="bold" sx={{ textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '1rem' }}>Zone-wise Verification Progress</Typography>
                  {adminZoneProgressOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </Box>
                <Collapse in={adminZoneProgressOpen}>
                  <Box>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  {zones.map((zone) => (
                    <Grid item xs={12} md={6} key={zone.zone}>
                      <Paper variant="outlined" sx={{ p: 2.5, '&:hover': { boxShadow: 2, borderColor: 'primary.main' } }}>
                        {/* Zone header */}
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <LocationIcon color="primary" />
                            <Typography variant="subtitle1" fontWeight="bold">{zone.zone}</Typography>
                          </Box>
                          <Chip label={`${zone.progressPercentage}%`} size="small" fontWeight="bold"
                            color={getProgressColor(zone.progressPercentage)} />
                        </Box>

                        {/* Progress bar */}
                        <LinearProgress variant="determinate" value={zone.progressPercentage}
                          color={getProgressColor(zone.progressPercentage)}
                          sx={{ height: 8, borderRadius: 4, bgcolor: 'grey.200', mb: 2 }} />

                        {/* Stats row */}
                        <Grid container spacing={1}>
                          {[
                            { label: 'Total',    value: zone.totalPersonnel, color: 'text.primary'  },
                            { label: 'Verified', value: zone.verified,       color: 'success.main'  },
                            { label: 'Pending',  value: zone.pending,        color: 'warning.main'  },
                          ].map(({ label, value, color }) => (
                            <Grid item xs={4} key={label}>
                              <Typography variant="caption" color="text.secondary">{label}</Typography>
                              <Typography variant="h6" fontWeight="bold" color={color}>
                                {value.toLocaleString()}
                              </Typography>
                            </Grid>
                          ))}
                        </Grid>

                        {/* Commands footer */}
                        <Box mt={1.5} pt={1.5} borderTop={1} borderColor="divider">
                          <Typography variant="caption" color="text.secondary">
                            {zone.completedCommands}/{zone.totalCommands} commands completed
                            {zone.inProgressCommands > 0 && ` • ${zone.inProgressCommands} in progress`}
                          </Typography>
                        </Box>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
                  </Box>
                </Collapse>
              </Paper>

              {/* -- Row 6: Command Search & Progress Detail -- */}
              <Paper sx={{ p: 3, mb: 4 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '1rem' }}>Command Search & Progress Detail</Typography>

                {/* Filters */}
                <Box display="flex" gap={2} mb={2}>
                  <TextField placeholder="Search commands..." value={search}
                    onChange={(e) => setSearch(e.target.value)} size="small" sx={{ flex: 1 }}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} />
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Zone</InputLabel>
                    <Select value={zoneFilter} label="Zone" onChange={(e) => setZoneFilter(e.target.value)}>
                      {uniqueZones.map((z) => <MenuItem key={z} value={z}>{z}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Box>

                    <TableContainer sx={{ maxHeight: 480 }}>
                      <Table stickyHeader size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>
                              <TableSortLabel active={sortBy==='zone'} direction={sortBy==='zone'?sortOrder:'asc'} onClick={()=>handleSort('zone')}>Zone</TableSortLabel>
                            </TableCell>
                            <TableCell>
                              <TableSortLabel active={sortBy==='name'} direction={sortBy==='name'?sortOrder:'asc'} onClick={()=>handleSort('name')}>Command</TableSortLabel>
                            </TableCell>
                            <TableCell align="center">Status</TableCell>
                            <TableCell align="right">
                              <TableSortLabel active={sortBy==='total'} direction={sortBy==='total'?sortOrder:'asc'} onClick={()=>handleSort('total')}>Total</TableSortLabel>
                            </TableCell>
                            <TableCell align="right">Verified</TableCell>
                            <TableCell align="right">Pending</TableCell>
                            <TableCell align="center">
                              <TableSortLabel active={sortBy==='progress'} direction={sortBy==='progress'?sortOrder:'asc'} onClick={()=>handleSort('progress')}>Progress</TableSortLabel>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filteredCommands.map((cmd) => (
                            <TableRow key={cmd.id} hover>
                              <TableCell><Chip label={cmd.zone} size="small" variant="outlined" /></TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight="bold">{cmd.name}</Typography>
                                <Typography variant="caption" color="text.secondary">{cmd.code}</Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Chip label={cmd.status?.replace('_',' ')} size="small" color={getStatusColor(cmd.status)} />
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>{cmd.totalPersonnel?.toLocaleString()}</TableCell>
                              <TableCell align="right" sx={{ color: 'success.main' }}>{cmd.verified?.toLocaleString()}</TableCell>
                              <TableCell align="right" sx={{ color: 'warning.main' }}>{cmd.pending?.toLocaleString()}</TableCell>
                              <TableCell>
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Box flex={1}>
                                    <LinearProgress variant="determinate" value={cmd.progressPercentage}
                                      color={getProgressColor(cmd.progressPercentage)}
                                      sx={{ height: 6, borderRadius: 3 }} />
                                  </Box>
                                  <Typography variant="caption" fontWeight="bold" sx={{ minWidth: 38, textAlign: 'right' }}>
                                    {cmd.progressPercentage}%
                                  </Typography>
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))}
                          {filteredCommands.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                                No commands found
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>

                  {/* -- Row 7: Recent Activity -- */}
                  <Paper sx={{ p: 3 }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '1rem' }}>Recent Activity</Typography>
                    {recentActivity.length === 0 ? (
                      <Box textAlign="center" py={4}>
                        <Typography color="text.secondary">No recent activity</Typography>
                      </Box>
                    ) : (
                      <List sx={{ maxHeight: 500, overflow: 'auto' }}>
                        {recentActivity.map((activity, index) => (
                          <React.Fragment key={activity.id}>
                            <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                              <ListItemAvatar>
                                <Avatar sx={{ bgcolor: 'success.light', width: 36, height: 36 }}>
                                  <CheckCircleIcon fontSize="small" />
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                primary={
                                  <Box component="span" display="flex" justifyContent="space-between" alignItems="center">
                                    <Typography component="span" variant="body2" fontWeight="bold">{activity.personnelName}</Typography>
                                    <Chip label={activity.zone} size="small" variant="outlined" sx={{ fontSize: 10 }} />
                                  </Box>
                                }
                                secondary={
                                  <Box component="span" display="flex" flexDirection="column">
                                    <Typography component="span" variant="caption" color="text.secondary" display="block">
                                      {activity.serviceNumber} • {activity.commandName}
                                    </Typography>
                                    <Typography component="span" variant="caption" color="text.secondary" display="block">
                                      By {activity.verifiedBy}
                                    </Typography>
                                    <Typography component="span" variant="caption" color="primary.main" display="block">
                                      {activity.timeAgo}
                                    </Typography>
                                  </Box>
                                }
                              />
                            </ListItem>
                            {index < recentActivity.length - 1 && <Divider component="li" />}
                          </React.Fragment>
                        ))}
                      </List>
                    )}
                  </Paper>
                </>
              )}
        </Box>

      </Container>
    );
  }

  // ========================================================
  // OFFICER DASHBOARD
  // ========================================================
  if (isOfficer) {
    if (officerLoading) {
      return <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh"><CircularProgress size={60} /></Box>;
    }

    const stats = officerData?.stats;
    const trend = officerData?.dailyTrend || [];
    const recent = officerData?.recentActivity || [];

    return (
      <Container maxWidth="xl">
        <Box sx={{ mt: 4, mb: 6 }}>

          {/* Header */}
          <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box display="flex" alignItems="center" gap={2.5}>
                {/* NIS Logo */}
                <Box
                  component="img"
                  src={nisLogoWithText}
                  alt="Nigeria Immigration Service"
                  sx={{
                    height: 50,
                    width: 'auto',
                    objectFit: 'contain',
                  }}
                />
                <Box>
                  <Typography 
                    variant="h6" 
                    fontWeight="600" 
                    sx={{ 
                      color: '#00a651', 
                      lineHeight: 1.2, 
                      mb: 0.5,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      fontSize: '1.1rem'
                    }}
                  >
                    Verification Officer Dashboard
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                    {officerData?.officer?.name || `${user?.firstName} ${user?.lastName}`}
                    {' '}• SN: <strong>{officerData?.officer?.serviceNumber || user?.serviceNumber || 'N/A'}</strong>
                    {' '}• Zone: <strong>{officerData?.officer?.assignedZone || user?.assignedZone || 'N/A'}</strong>
                    {officerData?.exercise?.name && <> • {officerData.exercise.name}</>}
                  </Typography>
                </Box>
              </Box>
              <Box display="flex" gap={2}>
                <IconButton onClick={fetchOfficerData} title="Refresh">
                  <RefreshIcon />
                </IconButton>
                <Button variant="outlined" color="error"
                  startIcon={<LogoutIcon />} onClick={handleLogout}>
                  Logout
                </Button>
              </Box>
            </Box>
          </Paper>

          {officerError && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setOfficerError(null)}>{officerError}</Alert>
          )}


          {/* Stat Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard title="My Total Verified" value={stats?.totalVerified?.toLocaleString() || '0'}
                icon={<CheckCircleIcon />} color="success" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard title="Verified Today" value={stats?.todayVerified?.toLocaleString() || '0'}
                subtitle="Today's count" icon={<TrendingIcon />} color="primary" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard title="This Week" value={stats?.weekVerified?.toLocaleString() || '0'}
                subtitle="Last 7 days" icon={<AssessmentIcon />} color="info" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard title="Zone Progress" value={`${stats?.zoneProgress || 0}%`}
                subtitle={`${stats?.zoneVerified || 0} of ${stats?.zonePersonnel || 0}`}
                icon={<PeopleIcon />} color="warning" />
            </Grid>
          </Grid>

          {/* -- Quick Actions row -- */}
          {officerCommands?.activeCommand?.status === 'IN_PROGRESS' && (
            <Box sx={{ mb: 3 }}>
              <Paper variant="outlined" sx={{
                p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                border: '1px solid', borderColor: 'grey.200', borderRadius: 2,
                cursor: 'pointer', '&:hover': { bgcolor: 'grey.50', borderColor: 'primary.light' }
              }}
                onClick={() => setTransferDialog({ open: true })}>
                <Box display="flex" alignItems="center" gap={2}>
                  <Box sx={{ p: 1, bgcolor: 'warning.50', borderRadius: 1 }}>
                    <SwapHorizIcon sx={{ color: 'warning.dark', fontSize: 22 }} />
                  </Box>
                  <Box>
                    <Typography variant="body2" fontWeight="bold">Personnel Transfer Request</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Can't find a personnel? Search exercise-wide and request a transfer
                    </Typography>
                  </Box>
                </Box>
                <Chip label={myTransferRequests.filter(r => r.status === 'PENDING').length > 0
                  ? `${myTransferRequests.filter(r => r.status === 'PENDING').length} Pending`
                  : 'New Request'}
                  size="small"
                  color={myTransferRequests.filter(r => r.status === 'PENDING').length > 0 ? 'warning' : 'default'}
                  variant="outlined" />
              </Paper>

            {/* Transfer dialog — inline under the card */}
            {/* -- Personnel Transfer Dialog -- */}
            {transferDialog.open && (
              <Paper elevation={3} sx={{ p: 3, mb: 3, border: '1px solid', borderColor: 'warning.300', borderRadius: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6" fontWeight="bold" sx={{ textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '1rem' }}>Personnel Transfer Request</Typography>
                  <IconButton size="small" onClick={() => {
                  setTransferDialog({ open: false }); setTransferSearch('');
                  setTransferResults([]); setTransferSelected(null); setTransferReason('');
                  }}><CloseIcon /></IconButton>
                </Box>

                <TextField fullWidth size="small" placeholder="Search by name, service number or IPPIS number..."
                value={transferSearch}
                onChange={(e) => handleTransferSearch(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                sx={{ mb: 2 }} />

                {transferSearching && <Box textAlign="center" py={2}><CircularProgress size={24} /></Box>}

                {!transferSearching && transferResults.length > 0 && !transferSelected && (
                  <Box sx={{ mb: 2, maxHeight: 280, overflowY: 'auto', border: '1px solid', borderColor: 'grey.200', borderRadius: 1 }}>
                    {transferResults.map((p) => (
                      <Box key={p.id}
                      onClick={() => p.isInMyCommand ? null : setTransferSelected(p)}
                      sx={{
                        p: 1.5, borderBottom: '1px solid', borderColor: 'grey.100',
                        cursor: p.isInMyCommand ? 'default' : 'pointer',
                        bgcolor: p.isInMyCommand ? 'success.50' : 'white',
                        '&:hover': { bgcolor: p.isInMyCommand ? 'success.50' : 'primary.50' }
                        }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box>
                            <Typography variant="body2" fontWeight="bold">{p.fullName}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {p.rankName} &nbsp;|&nbsp; SN: {p.serviceNumber}
                            </Typography>
                          </Box>
                          <Box textAlign="right">
                            {p.isInMyCommand ? (
                              <Chip label="In your command" size="small" color="success" variant="outlined" />
                          ) : (
                              <Box>
                                <Typography variant="caption" color="warning.dark" fontWeight="bold" display="block">
                                ⚠️ {p.currentCommand.code}
                                </Typography>
                                <Typography variant="caption" color="text.disabled" display="block">
                                  {p.currentCommand.zone}
                                </Typography>
                              </Box>
                          )}
                          </Box>
                        </Box>
                      </Box>
                  ))}
                  </Box>
              )}

                {!transferSearching && transferSearch.length >= 2 && transferResults.length === 0 && (
                  <Box textAlign="center" py={3} sx={{ bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">No personnel found</Typography>
                  </Box>
              )}

                {transferSelected && (
                  <Box sx={{ mb: 2, p: 2, bgcolor: 'warning.50', borderRadius: 1, border: '1px solid', borderColor: 'warning.200' }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
                      <Box>
                        <Typography variant="body1" fontWeight="bold">{transferSelected.fullName}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {transferSelected.rankName} &nbsp;|&nbsp; SN: {transferSelected.serviceNumber}
                        </Typography>
                        <Typography variant="caption" color="warning.dark" display="block">
                        Currently in: {transferSelected.currentCommand.name} ({transferSelected.currentCommand.zone})
                        </Typography>
                      </Box>
                      <Button size="small" onClick={() => setTransferSelected(null)} sx={{ textTransform: 'none', fontSize: '0.7rem' }}>
                      Change
                      </Button>
                    </Box>
                    <TextField fullWidth multiline rows={2} size="small"
                    label="Reason for transfer request *"
                    placeholder="e.g. Personnel physically present at this command with clearance papers"
                    value={transferReason}
                    onChange={(e) => setTransferReason(e.target.value)}
                    sx={{ mb: 1.5 }} />
                    <Box display="flex" gap={2}>
                      <Button variant="contained" color="warning" disabled={transferSubmitting || transferReason.trim().length < 10}
                      onClick={handleSubmitTransfer} sx={{ textTransform: 'none', fontWeight: 'bold' }}>
                        {transferSubmitting ? 'Submitting...' : 'Submit Transfer Request'}
                      </Button>
                      <Button variant="outlined" onClick={() => setTransferSelected(null)} sx={{ textTransform: 'none' }}>
                      Cancel
                      </Button>
                    </Box>
                  </Box>
              )}

                {myTransferRequests.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight="bold" display="block" sx={{ mb: 1 }}>
                    My Recent Transfer Requests
                    </Typography>
                    {myTransferRequests.slice(0, 5).map((r) => (
                      <Box key={r.id} display="flex" justifyContent="space-between" alignItems="center"
                      sx={{ py: 1, borderBottom: '1px solid', borderColor: 'grey.100' }}>
                        <Box>
                          <Typography variant="caption" fontWeight="bold">{r.personnel.fullName}</Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {r.fromCommand.code} → {r.toCommand.code} &nbsp;|&nbsp; {r.timeAgo}
                          </Typography>
                          {r.adminNote && (
                            <Typography variant="caption" color={r.status === 'APPROVED' ? 'success.dark' : 'error.dark'} display="block">
                            Admin: {r.adminNote}
                            </Typography>
                        )}
                        </Box>
                        <Chip
                        label={r.status}
                        size="small"
                        color={r.status === 'APPROVED' ? 'success' : r.status === 'REJECTED' ? 'error' : 'warning'}
                        variant="outlined"
                      />
                      </Box>
                  ))}
                  </Box>
              )}
              </Paper>
          )}

            </Box>
          )}

          {/* -- Active Command Panel — full width -- */}
          <Paper elevation={2} sx={{ p: 3, mb: 4, border: '1px solid', borderColor: 'grey.200' }}>

            {/* Title row */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography variant="h6" fontWeight="bold" sx={{ textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '1rem' }}>Current Active Command</Typography>
              <IconButton size="small" onClick={fetchOfficerCommands} title="Refresh">
                <RefreshIcon />
              </IconButton>
            </Box>

            {!officerCommands?.activeCommand ? (
              <Box textAlign="center" py={5}
                sx={{ bgcolor: 'grey.50', borderRadius: 2, border: '1px dashed', borderColor: 'grey.300' }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>No Active Command</Typography>
                <Typography variant="body2" color="text.disabled">
                  No command is currently open in your zone. Use the table below to request one.
                </Typography>
              </Box>
            ) : (
              <Box>

                {/* -- Row 1: Code · Name · Status Chip — all on one line -- */}
                <Box display="flex" alignItems="center" gap={2} mb={3} flexWrap="wrap">
                  <Typography variant="h5" fontWeight="bold" sx={{ minWidth: 'fit-content' }}>
                    {officerCommands.activeCommand.code}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ flex: 1 }}>
                    {officerCommands.activeCommand.name}
                  </Typography>
                  <Chip
                    label={
                      officerCommands.activeCommand.status === 'PENDING_OPEN'       ? 'Awaiting Approval to Start' :
                      officerCommands.activeCommand.status === 'PENDING_COMPLETION' ? 'Awaiting Approval to Close' :
                                                                                      'In Progress'
                    }
                    color={officerCommands.activeCommand.status === 'IN_PROGRESS' ? 'success' : 'warning'}
                    variant="outlined"
                    sx={{ fontWeight: 'bold', flexShrink: 0 }}
                  />
                </Box>

                {/* -- Row 2: 4 stat cards — full width -- */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  {[
                    { label: 'Total Personnel', value: officerCommands.activeCommand.totalPersonnel, color: 'text.primary'  },
                    { label: 'Verified',         value: officerCommands.activeCommand.verified,       color: 'success.main' },
                    { label: 'Pending',          value: officerCommands.activeCommand.pending,        color: 'warning.main' },
                    { label: 'Progress',         value: `${officerCommands.activeCommand.progressPercentage}%`, color: 'primary.main' },
                  ].map((stat) => (
                    <Grid item xs={6} sm={3} key={stat.label}>
                      <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', borderColor: 'grey.200', borderRadius: 2 }}>
                        <Typography variant="h4" fontWeight="bold" color={stat.color}>
                          {stat.value}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">{stat.label}</Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>

                {/* -- Row 3: Progress bar -- */}
                <Box sx={{ mb: 3 }}>
                  <Box display="flex" justifyContent="space-between" mb={0.5}>
                    <Typography variant="caption" color="text.secondary">Verification Progress</Typography>
                    <Typography variant="caption" fontWeight="bold">
                      {officerCommands.activeCommand.progressPercentage}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={officerCommands.activeCommand.progressPercentage}
                    color={
                      officerCommands.activeCommand.progressPercentage >= 80 ? 'success' :
                      officerCommands.activeCommand.progressPercentage >= 50 ? 'warning' : 'primary'
                    }
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* -- Row 4: Action area -- */}
                {officerCommands.activeCommand.status === 'PENDING_OPEN' && (
                  <Box sx={{ p: 2, bgcolor: 'warning.50', borderRadius: 2, border: '1px solid', borderColor: 'warning.200' }}>
                    <Typography variant="body2" color="warning.dark">
                      ⏳ Your request to start <strong>{officerCommands.activeCommand.name}</strong> has been submitted. Waiting for Admin approval.
                    </Typography>
                  </Box>
                )}
                {officerCommands.activeCommand.status === 'IN_PROGRESS' && (
                  <Box display="flex" gap={2} flexWrap="wrap">
                    <Button variant="contained" color="success"
                      startIcon={<VerificationIcon />}
                      onClick={() => navigate('/verification')}
                      sx={{ textTransform: 'none', fontWeight: 'bold', px: 3 }}>
                      Start Verifying Personnel
                    </Button>
                    <Button variant="outlined" color="warning"
                      onClick={() => setCompletionDialog({ open: true, command: officerCommands.activeCommand })}
                      sx={{ textTransform: 'none', px: 3 }}>
                      Request Completion
                    </Button>
                  </Box>
                )}
                {officerCommands.activeCommand.status === 'PENDING_COMPLETION' && (
                  <Box sx={{ p: 2, bgcolor: 'warning.50', borderRadius: 2, border: '1px solid', borderColor: 'warning.200' }}>
                    <Typography variant="body2" color="warning.dark">
                      ⏳ Completion request submitted for <strong>{officerCommands.activeCommand.name}</strong>. Waiting for Admin to approve and close.
                    </Typography>
                  </Box>
                )}

              </Box>
            )}
          </Paper>


          {/* Open Request Dialog */}
          {openRequestDialog.open && (

          <Paper sx={{ p: 3, mb: 3, border: '2px solid', borderColor: 'primary.main', bgcolor: 'primary.50' }}>
              <Typography variant="h6" fontWeight="bold" color="primary.dark" gutterBottom>
                📋 Request to Start Command
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                You are requesting to start verification for <strong>{openRequestDialog.command?.name}</strong>.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                This request will be sent to the Admin for approval. Once approved, your team can begin verifying {openRequestDialog.command?.totalPersonnel} personnel in this command.
              </Typography>
              <Box display="flex" gap={2}>
                <Button variant="contained" color="primary" disabled={completionLoading}
                  onClick={() => handleRequestOpen(openRequestDialog.command?.id)}
                  sx={{ textTransform: 'none' }}>
                  {completionLoading ? 'Submitting...' : 'Submit Request'}
                </Button>
                <Button variant="outlined"
                  onClick={() => setOpenRequestDialog({ open: false, command: null })}
                  sx={{ textTransform: 'none' }}>Cancel</Button>
              </Box>
            </Paper>
          )}

          {/* Completion Request Confirm */}
          {completionDialog.open && (

          <Paper sx={{ p: 3, mb: 3, border: '2px solid', borderColor: 'warning.main', bgcolor: 'warning.50' }}>
              <Typography variant="h6" fontWeight="bold" color="warning.dark" gutterBottom>
                ⚠️ Request Command Completion
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                You are requesting to close <strong>{completionDialog.command?.name}</strong>.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {completionDialog.command?.pending > 0
                  ? `⚠️ ${completionDialog.command.pending} personnel are still unverified. Remaining personnel will be marked "Did Not Appear" when admin closes this command.`
                  : '✅ All personnel have been verified. Ready to close.'}
              </Typography>
              <Box display="flex" gap={2}>
                <Button variant="contained" color="warning" disabled={completionLoading}
                  onClick={() => handleRequestCompletion(completionDialog.command?.id)}
                  sx={{ textTransform: 'none' }}>
                  {completionLoading ? 'Submitting...' : 'Confirm Request'}
                </Button>
                <Button variant="outlined" onClick={() => setCompletionDialog({ open: false, command: null })}
                  sx={{ textTransform: 'none' }}>Cancel</Button>
              </Box>
            </Paper>
          )}

          {/* -- ALL COMMANDS IN ZONE — Overview (read-only) -- */}

          <Paper sx={{ p: 3, mb: 4 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              📋 All Commands in My Zone
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell><strong>Command</strong></TableCell>
                    <TableCell align="right"><strong>Total</strong></TableCell>
                    <TableCell align="right"><strong>Verified</strong></TableCell>
                    <TableCell align="right"><strong>Pending</strong></TableCell>
                    <TableCell><strong>Progress</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell align="center"><strong>Action</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(officerCommands?.allCommands || []).map((cmd) => (
                    <TableRow key={cmd.id}
                      sx={{ bgcolor: cmd.status === 'IN_PROGRESS' ? 'primary.50' :
                                     cmd.status === 'COMPLETED' ? 'success.50' :
                                     cmd.status === 'PENDING_COMPLETION' ? 'warning.50' : 'inherit' }}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={cmd.status === 'IN_PROGRESS' ? 'bold' : 'normal'}>
                          {cmd.code}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">{cmd.name}</Typography>
                      </TableCell>
                      <TableCell align="right">{cmd.totalPersonnel}</TableCell>
                      <TableCell align="right" sx={{ color: 'success.main', fontWeight: 'bold' }}>{cmd.verified}</TableCell>
                      <TableCell align="right" sx={{ color: cmd.pending > 0 ? 'warning.main' : 'inherit' }}>{cmd.pending}</TableCell>
                      <TableCell sx={{ minWidth: 140 }}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Box flex={1}>
                            <LinearProgress variant="determinate" value={cmd.progressPercentage}
                              color={cmd.progressPercentage >= 80 ? 'success' : cmd.progressPercentage >= 50 ? 'warning' : 'error'}
                              sx={{ height: 6, borderRadius: 3 }} />
                          </Box>
                          <Typography variant="caption" fontWeight="bold">{cmd.progressPercentage}%</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip size="small"
                          label={cmd.status === 'PENDING_OPEN' ? '⏳ Pending Start' :
                                 cmd.status === 'IN_PROGRESS' ? '🟢 Active' :
                                 cmd.status === 'PENDING_COMPLETION' ? '⏳ Pending Close' :
                                 cmd.status === 'COMPLETED' ? '✅ Done' : '⏸️ Not Started'}
                          color={cmd.status === 'PENDING_OPEN' ? 'warning' :
                                 cmd.status === 'IN_PROGRESS' ? 'primary' :
                                 cmd.status === 'PENDING_COMPLETION' ? 'warning' :
                                 cmd.status === 'COMPLETED' ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="center">
                        {cmd.status === 'NOT_STARTED' && !officerCommands?.activeCommand && (
                          <Button size="small" variant="outlined" color="primary"
                            onClick={() => setOpenRequestDialog({ open: true, command: cmd })}
                            sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
                            Request to Start
                          </Button>
                        )}
                        {cmd.status === 'NOT_STARTED' && officerCommands?.activeCommand && (
                          <Typography variant="caption" color="text.disabled">Waiting</Typography>
                        )}
                        {cmd.status === 'PENDING_OPEN' && (
                          <Typography variant="caption" color="warning.main" fontWeight="bold">⏳ Pending</Typography>
                        )}
                        {cmd.status === 'IN_PROGRESS' && (
                          <Typography variant="caption" color="success.main" fontWeight="bold">🟢 Active</Typography>
                        )}
                        {cmd.status === 'PENDING_COMPLETION' && (
                          <Typography variant="caption" color="warning.main" fontWeight="bold">⏳ Pending Close</Typography>
                        )}
                        {cmd.status === 'COMPLETED' && (
                          <Typography variant="caption" color="text.disabled">✅ Done</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Zone Progress + 7-Day Trend */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Zone {officerData?.officer?.assignedZone} Progress
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2">Overall Progress</Typography>
                    <Typography variant="body2" fontWeight="bold">{stats?.zoneProgress || 0}%</Typography>
                  </Box>
                  <LinearProgress variant="determinate" value={stats?.zoneProgress || 0}
                    color={stats?.zoneProgress >= 80 ? 'success' : stats?.zoneProgress >= 50 ? 'warning' : 'error'}
                    sx={{ height: 10, borderRadius: 5, mb: 3 }} />

                  {[
                    { label: 'Total Personnel', value: stats?.zonePersonnel || 0, color: 'text.primary' },
                    { label: 'Verified',         value: stats?.zoneVerified || 0,  color: 'success.main' },
                    { label: 'Pending',          value: stats?.zonePending || 0,   color: 'warning.main' },
                    { label: 'Commands',         value: stats?.totalCommands || 0, color: 'info.main'    },
                  ].map(({ label, value, color }) => (
                    <Box key={label} display="flex" justifyContent="space-between" py={1}
                      sx={{ borderBottom: 1, borderColor: 'divider' }}>
                      <Typography variant="body2" color="text.secondary">{label}</Typography>
                      <Typography variant="body2" fontWeight="bold" color={color}>{value}</Typography>
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '1rem' }}>My 7-Day Verification Trend</Typography>
                {trend.length > 0 ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={trend}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }}
                        tickFormatter={(d) => { const dt = new Date(d); return `${dt.getMonth()+1}/${dt.getDate()}`; }} />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip labelFormatter={(d) => new Date(d).toLocaleDateString()} />
                      <Line type="monotone" dataKey="count" stroke="#2e7d32" strokeWidth={2}
                        dot={{ fill: '#2e7d32', r: 5 }} name="Verified" />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <Box textAlign="center" py={4}>
                    <Typography color="text.secondary">No verification data yet</Typography>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '1rem' }}>My Recent Verifications</Typography>
            {recent.length === 0 ? (
              <Box textAlign="center" py={4}>
                <Typography color="text.secondary">No verifications yet</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Use the <strong>Current Active Command</strong> panel above to start verifying.
                </Typography>
              </Box>
            ) : (
              <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                {recent.map((activity, index) => (
                  <React.Fragment key={activity.id}>
                    <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'success.light', width: 36, height: 36 }}>
                          <CheckCircleIcon fontSize="small" />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box component="span" display="flex" justifyContent="space-between" alignItems="center">
                            <Typography component="span" variant="body2" fontWeight="bold">
                              {activity.personnelName}
                            </Typography>
                            <Chip label={activity.zone} size="small" variant="outlined" sx={{ fontSize: 10 }} />
                          </Box>
                        }
                        secondary={
                          <Box component="span" display="flex" flexDirection="column">
                            <Typography component="span" variant="caption" color="text.secondary" display="block">
                              {activity.serviceNumber} • {activity.commandName}
                            </Typography>
                            <Typography component="span" variant="caption" color="text.secondary" display="block">
                              Ref: {activity.referenceNumber}
                            </Typography>
                            <Typography component="span" variant="caption" color="primary.main" display="block">
                              {activity.timeAgo}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < recent.length - 1 && <Divider component="li" />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </Paper>

        </Box>

      </Container>
    );
  }

  return null;
};

export default DashboardPage;
